import { useEffect, useState } from "react";
import api from "../lib/api";
import { Bell, X, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const supported = () => typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
const DISMISS_KEY = "edurise_push_prompt_dismissed";

const b64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

async function registerPush() {
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.public_key) });
  await api.post("/push/subscribe", sub.toJSON());
}

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      const t = ctx.currentTime + i * 0.16;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.connect(g).connect(ctx.destination); o.start(t); o.stop(t + 0.32);
    });
  } catch (_) { /* audio blocked */ }
};

export const SoundToggle = () => {
  const [sound, setSound] = useState(null);
  useEffect(() => { api.get("/push/settings").then(r => setSound(r.data.sound)).catch(() => {}); }, []);
  const toggle = async () => {
    const next = !sound;
    setSound(next);
    try {
      await api.put("/push/settings", { sound: next });
      toast.success(next ? "通知声音已开启" : "通知已静音");
      if (next) playChime();
    } catch (_) { setSound(!next); toast.error("设置失败，请重试"); }
  };
  if (sound === null) return null;
  return (
    <button data-testid="notify-sound-toggle" onClick={toggle}
      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-sm transition-colors">
      {sound ? <Volume2 size={15} className="text-emerald-700" /> : <VolumeX size={15} className="text-slate-400" />}
      <span data-testid="notify-sound-state">通知声音：{sound ? "开" : "静音"}</span>
    </button>
  );
};

export const PushPrompt = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => {
      if (e.data?.type === "crm-new-message" && e.data.sound && document.visibilityState === "visible") playChime();
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!supported()) return;
    if (Notification.permission === "granted") registerPush().catch(() => {});
    else if (Notification.permission === "default" && !localStorage.getItem(DISMISS_KEY)) setShow(true);
  }, []);

  const allow = async () => {
    setShow(false);
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { toast.error("您没有允许通知，可以稍后在浏览器设置里开启"); return; }
    try { await registerPush(); toast.success("通知已开启，新消息会弹出提醒"); }
    catch (e) { toast.error("通知开启失败，请稍后再试"); }
  };

  const later = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); };

  if (!show) return null;
  return (
    <div data-testid="push-prompt" className="fixed bottom-4 right-4 z-50 w-[320px] max-w-[calc(100vw-2rem)] bg-slate-950 text-white rounded-sm shadow-2xl p-4">
      <button data-testid="push-prompt-close" onClick={later} className="absolute top-2 right-2 text-slate-400 hover:text-white" aria-label="关闭"><X size={14} /></button>
      <div className="flex items-start gap-3">
        <div className="p-2 bg-emerald-600 rounded-sm"><Bell size={16} /></div>
        <div className="flex-1">
          <div className="font-semibold text-sm">开启新消息通知？</div>
          <p className="text-xs text-slate-300 mt-1">WhatsApp、Messenger、Instagram 有新消息时弹出提醒，CRM 关掉了也能收到。</p>
          <div className="flex gap-2 mt-3">
            <button data-testid="push-prompt-allow" onClick={allow} className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 rounded-sm">允许通知</button>
            <button data-testid="push-prompt-later" onClick={later} className="px-3 py-1.5 text-xs text-slate-300 hover:text-white">以后再说</button>
          </div>
        </div>
      </div>
    </div>
  );
};
